import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Web Fullscreen API toggle for a single element (the call stage). On native the
 * overlay already fills the screen, so callers gate the control on
 * `supported`. Returns a ref to attach to the element that should go fullscreen.
 */
export function useFullscreen<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)

  const supported = typeof document !== 'undefined' && Boolean(document.fullscreenEnabled)

  const toggle = useCallback(() => {
    if (typeof document === 'undefined') return
    try {
      if (!document.fullscreenElement) {
        void ref.current?.requestFullscreen?.().catch(() => {})
      } else {
        void document.exitFullscreen?.().catch(() => {})
      }
    } catch {
      /* fullscreen not permitted */
    }
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  return { ref, isFullscreen, supported, toggle }
}
